import { getErrorMessage } from '../../utils/serviceUtils';
import { randomId } from '../../utils/randomId';
import React from 'react';
import { GameState } from '../../types/game';
import { GameAction } from '../gameReducer';
import { perf } from '../../utils/perfMetrics';
import { calculateBankBlockedReason } from '../../domain/potential';
import { canRescueByFive } from '../../domain/dice';
import { useOptimizedPotential, UseOptimizedPotentialReturn } from './useOptimizedSelectors';
import { isVerboseLoggingEnabled, logger } from '../../utils/logger';
import { recordDebugTrace } from '../debug/debugLog';
import { useSound } from '../SoundContext';
import { formatPersistenceErrorMessage, useTurnPersistence, TurnPersistenceError } from './useTurnPersistence';
import { useTurnGuards } from './useTurnGuards';
import { gameRules, makeEmptyDiceMask } from '../../domain/gameRules';
import { computeToggleKeepResult } from '../handlers/turn/turnKeepHandlers';
import { sendOnlineCommand } from '../../services/transport/onlineTransportMirror';
import { isDevEnv } from '../../utils/debugFlags';
import {
  DEV_PLAY_AS_SPECTATOR_CHANGE_EVENT,
  readDevPlayAsSpectatorPreference,
} from '../../utils/devSpectatorMode';

const AUTO_BUST_END_TURN_DELAY_MS = 1300;

function dequeueDeterministicTestDice(): number[] | null {
  if (typeof window === 'undefined') return null;
  const queue = window.__MOCK_DICE_QUEUE__;
  if (!Array.isArray(queue) || queue.length === 0) return null;
  const next = queue.shift();
  if (!Array.isArray(next) || next.length !== gameRules.dice.count) return null;
  const normalized = next.map((value) => Number(value));
  const valid = normalized.every((value) => Number.isInteger(value) && value >= 1 && value <= gameRules.dice.faces);
  return valid ? normalized : null;
}

function cloneMask(mask: boolean[] | undefined): boolean[] {
  if (!Array.isArray(mask) || mask.length !== gameRules.dice.count) {
    return makeEmptyDiceMask();
  }
  return mask.map((flag) => Boolean(flag));
}

function masksEqual(a: boolean[] | null | undefined, b: boolean[] | null | undefined): boolean {
  if (!a || !b) return false;
  if (a.length !== b.length) return false;
  return a.every((value, index) => value === b[index]);
}

function resolveKeepRollIndex(state: GameState): number {
  const turnCycle = state.turnCycle;
  if (!turnCycle) return -1;
  if (typeof turnCycle.lastRoll?.rollIndex === 'number') {
    return turnCycle.lastRoll.rollIndex;
  }
  return turnCycle.rollCount > 0 ? turnCycle.rollCount - 1 : -1;
}

interface Params {
  gameState: GameState;
  dispatch: React.Dispatch<GameAction>;
  currentClientPlayerId: string | null;
  setCurrentClientPlayerId: (value: string | null | string) => void;
  isHost: boolean;
  addScore: (playerId: string, points: number, turn?: number) => void;
  rpcLock?: { active: boolean; type: string | null; eventId: string | null; startedAt: number | null };
  withRpcLock?: <T>(label: string, eventId: string | null, fn: () => Promise<T>) => Promise<T>;
  refreshSnapshot?: (reason: string) => Promise<boolean>;
}

export interface TurnBundle {
  rollDice: () => void;
  toggleKeepDie: (index: number) => void;
  endTurn: () => void;
  undoLastTurn?: () => void;
  potential: UseOptimizedPotentialReturn;
  bankBlockedReason: string | null;
  rescueByFiveAvailable: boolean;
  rpcLock: { active: boolean; type: string | null; eventId: string | null; startedAt: number | null };
}

export function useTurnActions({
  gameState,
  dispatch,
  currentClientPlayerId,
  setCurrentClientPlayerId,
  isHost,
  addScore: _addScore,
  rpcLock = { active: false, type: null, eventId: null, startedAt: null },
  withRpcLock = async <T,>(_label: string, _eventId: string | null, fn: () => Promise<T>) => fn(),
  refreshSnapshot,
}: Params): TurnBundle {
  const devMode = isDevEnv();
  const [devPlayAsSpectator, setDevPlayAsSpectator] = React.useState<boolean>(
    () => devMode && readDevPlayAsSpectatorPreference(),
  );
  const bustTimerRef = React.useRef<NodeJS.Timeout | null>(null);
  const lastAutoBustEventRef = React.useRef<string | null>(null);
  const lastSpectatorAutoTurnRef = React.useRef<string | null>(null);
  const keepIntentInFlightRef = React.useRef<boolean>(false);
  const optimisticKeptMaskRef = React.useRef<boolean[] | null>(null);
  const queuedKeepMaskRef = React.useRef<boolean[] | null>(null);
  const keepEpochRef = React.useRef<string | null>(null);
  const { playSound } = useSound();
  // Ref do aktualnego gameState (zawsze najświeższy)
  const gameStateRef = React.useRef(gameState);
  React.useEffect(() => {
    gameStateRef.current = gameState;
  }, [gameState]);
  React.useEffect(() => {
    return () => {
      if (bustTimerRef.current) {
        clearTimeout(bustTimerRef.current);
        bustTimerRef.current = null;
      }
    };
  }, []);

  const { recoverFromAppendError } = useTurnPersistence({ gameStateRef, dispatch, refreshSnapshot });
  const { ensureActivePlayer, isRpcLocked } = useTurnGuards({ rpcLock, isHost, setCurrentClientPlayerId });
  const refreshSnapshotAfterCommandFailure = React.useCallback(
    (reason: string, transportCode: string | null, transportReason: string | null) => {
      if (!refreshSnapshot) return;
      const shouldRefresh = transportReason === 'ACK_TIMEOUT'
        || transportCode === 'ACTIVE_PLAYER_MISMATCH'
        || transportCode === 'KEEP_REQUIRED_BEFORE_ROLL'
        || transportCode === 'ROLL_INDEX_OUT_OF_SYNC';
      if (!shouldRefresh) return;
      void refreshSnapshot(reason);
    },
    [refreshSnapshot],
  );

  React.useEffect(() => {
    const turnCycle = gameState.turnCycle;
    if (gameState.mode === 'REAL' || !turnCycle) {
      optimisticKeptMaskRef.current = null;
      queuedKeepMaskRef.current = null;
      keepEpochRef.current = null;
      return;
    }
    const keepRollIndex = typeof turnCycle.lastRoll?.rollIndex === 'number'
      ? turnCycle.lastRoll.rollIndex
      : (turnCycle.rollCount > 0 ? turnCycle.rollCount - 1 : -1);
    const epoch = `${gameState.id}:${gameState.turnNumber}:${turnCycle.activePlayerId || ''}:${keepRollIndex}`;
    const capturedMask = cloneMask(turnCycle.capturedMask);
    const authoritativeKept = cloneMask(turnCycle.kept).map((flag, index) => flag || capturedMask[index]);
    if (keepEpochRef.current !== epoch) {
      keepEpochRef.current = epoch;
      optimisticKeptMaskRef.current = authoritativeKept;
      queuedKeepMaskRef.current = null;
      return;
    }
    if (!keepIntentInFlightRef.current && !queuedKeepMaskRef.current) {
      optimisticKeptMaskRef.current = authoritativeKept;
    }
  }, [gameState.id, gameState.mode, gameState.turnCycle, gameState.turnNumber]);

  const flushQueuedKeepIntent = React.useCallback(function flushQueuedKeepIntentImpl() {
    if (keepIntentInFlightRef.current) return;
    const pendingMask = queuedKeepMaskRef.current;
    if (!pendingMask) return;

    const currentState = gameStateRef.current;
    if (currentState.mode === 'REAL' || !currentState.turnCycle) {
      queuedKeepMaskRef.current = null;
      optimisticKeptMaskRef.current = null;
      return;
    }

    const activePlayerId = currentState.turnCycle.activePlayerId;
    if (!activePlayerId || activePlayerId !== currentClientPlayerId) return;
    const capturedMask = cloneMask(currentState.turnCycle.capturedMask);
    const normalizedMask = pendingMask.map((flag, index) => flag || capturedMask[index]);

    const keepRollIndex = resolveKeepRollIndex(currentState);
    if (keepRollIndex < 0) {
      logger.debug('KEEP_BLOCKED_NO_ROLL_INDEX');
      return;
    }

    keepIntentInFlightRef.current = true;
    queuedKeepMaskRef.current = null;
    const keepIntentId = randomId();

    void withRpcLock('DICE_KEEP', keepIntentId, async () => {
      const ok = await sendOnlineCommand(currentState.id, {
        type: 'KEEP_INTENT',
        eventId: keepIntentId,
        playerId: activePlayerId,
        keptMask: normalizedMask,
      });
      if (!ok) {
        throw new Error('KEEP_INTENT_REJECTED');
      }
    }).catch((err) => {
      const lastTransportError = typeof window !== 'undefined'
        ? window.__LAST_PARTYKIT_COMMAND_ERROR__
        : undefined;
      logger.error('KEEP_INTENT_FAILED', {
        error: err instanceof Error ? err.message : String(err),
        gameId: currentState.id,
        turn: currentState.turnNumber,
        playerId: activePlayerId,
        eventId: keepIntentId,
        transportReason: lastTransportError?.reason ?? null,
        transportCode: lastTransportError?.code ?? null,
        transportSource: lastTransportError?.source ?? null,
      });
      refreshSnapshotAfterCommandFailure(
        'turn-keep-rejected',
        lastTransportError?.code ?? null,
        lastTransportError?.reason ?? null,
      );
    }).finally(() => {
      keepIntentInFlightRef.current = false;
      const queuedMask = queuedKeepMaskRef.current;
      if (!queuedMask) return;
      if (masksEqual(queuedMask, normalizedMask)) {
        queuedKeepMaskRef.current = null;
        return;
      }
      flushQueuedKeepIntentImpl();
    });
  }, [currentClientPlayerId, refreshSnapshotAfterCommandFailure, withRpcLock]);

  const endTurn = React.useCallback((options?: { allowWithoutRoll?: boolean; allowHostMismatch?: boolean }): boolean => {
    // ✅ Czytaj ŚWIEŻY gameState z ref (nie z closure!)
    const currentGameState = gameStateRef.current;
    
    if (currentGameState.mode === 'REAL' || !currentGameState.turnCycle) return false;
    if (currentGameState.isGameOver || Boolean(currentGameState.winner)) {
      if (isVerboseLoggingEnabled()) {
        logger.debug('END_TURN_GUARD_GAME_OVER', {
          gameId: currentGameState.id,
          turn: currentGameState.turnNumber,
          activePlayerId: currentGameState.turnCycle.activePlayerId || null,
        });
      }
      return false;
    }
    if (!ensureActivePlayer(currentGameState, currentClientPlayerId, {
      allowHostMismatch: options?.allowHostMismatch,
    })) return false;
    if (bustTimerRef.current) {
      clearTimeout(bustTimerRef.current);
      bustTimerRef.current = null;
    }
    const { dice, activePlayerId } = currentGameState.turnCycle;
    if (!activePlayerId) {
      if (isVerboseLoggingEnabled()) {
        logger.warn('END_TURN_GUARD_MISSING_ACTIVE_PLAYER', { currentClientPlayerId });
      }
      return false;
    }
    if (!options?.allowWithoutRoll && (!dice || dice.length === 0)) return false;
    perf.mark('endTurn');

    const endTurnIntentId = randomId();
    const forceEphemeral = Boolean(options?.allowHostMismatch);
    const persistTurnIntent = async () => {
      try {
        dispatch({ type: 'MARK_TURN_FINALIZING' });
        await withRpcLock('TURN_END', endTurnIntentId, async () => {
          const command = {
            type: 'END_TURN_INTENT' as const,
            eventId: endTurnIntentId,
            playerId: activePlayerId,
          };
          const ok = forceEphemeral
            ? await sendOnlineCommand(currentGameState.id, command, undefined, { preferEphemeral: true })
            : await sendOnlineCommand(currentGameState.id, command);
          if (!ok) {
            throw new TurnPersistenceError('TURN_END', 'END_TURN_INTENT_REJECTED');
          }
        });
      } catch (error) {
        logger.error('END_TURN_PERSISTENCE_ERROR', {
          gameId: currentGameState.id,
          turn: currentGameState.turnNumber,
          endEventId: endTurnIntentId,
          error,
        });

        if (typeof window !== 'undefined') {
          window.__LAST_TURN_ERROR__ = {
            stage: error instanceof TurnPersistenceError ? error.stage : 'UNKNOWN',
            message: getErrorMessage(error),
            endEventId: endTurnIntentId,
            timestamp: Date.now(),
          };
        }

        const rawMessage = getErrorMessage(error);
        const lastTransportError = typeof window !== 'undefined'
          ? window.__LAST_PARTYKIT_COMMAND_ERROR__
          : undefined;
        const isBenignSpectatorMismatch = Boolean(
          devPlayAsSpectator
          && options?.allowWithoutRoll
          && lastTransportError?.code === 'ACTIVE_PLAYER_MISMATCH'
        );
        if (isBenignSpectatorMismatch) {
          // Another actor likely advanced turn first; avoid noisy notices and retry loops.
          refreshSnapshotAfterCommandFailure(
            'turn-end-active-player-mismatch',
            lastTransportError?.code ?? null,
            lastTransportError?.reason ?? null,
          );
          return;
        }
        const friendly = formatPersistenceErrorMessage(rawMessage);
        recordDebugTrace(
          'TURN_PERSISTENCE_ERROR',
          {
            stage: error instanceof TurnPersistenceError ? error.stage : 'UNKNOWN',
            rawMessage,
            friendlyMessage: friendly,
            gameId: currentGameState.id,
            turn: currentGameState.turnNumber,
            activePlayerId,
            currentClientPlayerId,
            eventId: endTurnIntentId,
            transportReason: lastTransportError?.reason ?? null,
            transportCode: lastTransportError?.code ?? null,
            transportSource: lastTransportError?.source ?? null,
          },
          { source: 'turn', tags: ['rpc', 'error'] },
          { force: true, flushReason: 'turn-persist-error' }
        );
        const noticeId = `notice-${Date.now()}`;
        dispatch({
          type: 'ADD_NOTICE',
          notice: {
            id: noticeId,
            playerId: activePlayerId,
            message: friendly,
            kind: 'error',
            ts: Date.now(),
          },
        });
        const globalNoticeId = `${noticeId}-global`;
        dispatch({
          type: 'ADD_NOTICE',
          notice: {
            id: globalNoticeId,
            message: friendly,
            kind: 'error',
            ts: Date.now(),
          },
        });
        setTimeout(() => {
          dispatch({ type: 'CLEAR_NOTICE', id: noticeId });
          dispatch({ type: 'CLEAR_NOTICE', id: globalNoticeId });
        }, 8000);
        if (friendly.includes('1000')) {
          playSound('fury');
        }
        refreshSnapshotAfterCommandFailure(
          'turn-end-rejected',
          lastTransportError?.code ?? null,
          lastTransportError?.reason ?? null,
        );
        const nonRetryableSpectatorCodes = new Set([
          'GAME_OVER',
          'ACTIVE_PLAYER_MISMATCH',
          'PLAYER_ID_MISMATCH',
          'PLAYER_NOT_ACTIVE',
          'AUTH_CONTEXT_MISSING',
          'HOST_REQUIRED',
        ]);
        const shouldRetrySpectatorAutoTurn = devPlayAsSpectator
          && options?.allowWithoutRoll
          && lastTransportError?.reason !== 'ACK_TIMEOUT'
          && !nonRetryableSpectatorCodes.has(lastTransportError?.code || '')
          && !currentGameState.isGameOver
          && !currentGameState.winner;
        if (shouldRetrySpectatorAutoTurn) {
          // Allow automatic spectator end-turn flow to retry within the same turn.
          lastSpectatorAutoTurnRef.current = null;
        }
        void recoverFromAppendError(rawMessage);
      } finally {
        perf.end('endTurn');
      }
    };

    void persistTurnIntent();
    return true;
  }, [
    devPlayAsSpectator,
    currentClientPlayerId,
    dispatch,
    ensureActivePlayer,
    playSound,
    recoverFromAppendError,
    refreshSnapshotAfterCommandFailure,
    withRpcLock,
  ]);

  React.useEffect(() => {
    if (!devMode || typeof window === 'undefined') return;
    const sync = () => setDevPlayAsSpectator(readDevPlayAsSpectatorPreference());
    sync();
    window.addEventListener(DEV_PLAY_AS_SPECTATOR_CHANGE_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(DEV_PLAY_AS_SPECTATOR_CHANGE_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, [devMode]);

  React.useEffect(() => {
    const tc = gameState.turnCycle;
    const gameEnded = gameState.isGameOver || Boolean(gameState.winner);
    if (gameState.mode === 'REAL' || !tc?.activePlayerId || !tc.lastRoll || gameEnded) {
      if (bustTimerRef.current) {
        clearTimeout(bustTimerRef.current);
        bustTimerRef.current = null;
      }
      return;
    }

    const isClientActive = tc.activePlayerId === currentClientPlayerId;
    const didBust = tc.lastRoll.didBust === true;
    if (!didBust || !isClientActive) {
      if (bustTimerRef.current) {
        clearTimeout(bustTimerRef.current);
        bustTimerRef.current = null;
      }
      if (!didBust) {
        lastAutoBustEventRef.current = null;
      }
      return;
    }

    const autoKey = `${gameState.id}:${gameState.turnNumber}:${tc.lastRoll.eventId || tc.rollCount}`;
    if (lastAutoBustEventRef.current === autoKey) {
      return;
    }
    lastAutoBustEventRef.current = autoKey;

    if (bustTimerRef.current) {
      clearTimeout(bustTimerRef.current);
      bustTimerRef.current = null;
    }

    bustTimerRef.current = setTimeout(() => {
      bustTimerRef.current = null;
      const current = gameStateRef.current;
      const currentTurnCycle = current.turnCycle;
      if (current.isGameOver || Boolean(current.winner)) return;
      if (!currentTurnCycle?.lastRoll?.didBust) return;
      if (currentTurnCycle.activePlayerId !== currentClientPlayerId) return;
      endTurn();
    }, AUTO_BUST_END_TURN_DELAY_MS);
  }, [
    currentClientPlayerId,
    endTurn,
    gameState.id,
    gameState.isGameOver,
    gameState.mode,
    gameState.turnCycle,
    gameState.turnNumber,
    gameState.winner,
  ]);

  React.useEffect(() => {
    if (!devPlayAsSpectator) {
      lastSpectatorAutoTurnRef.current = null;
      return;
    }
    const gameEnded = gameState.isGameOver || Boolean(gameState.winner);
    if (gameEnded) {
      lastSpectatorAutoTurnRef.current = null;
      return;
    }
    const tc = gameState.turnCycle;
    if (gameState.mode === 'REAL' || !tc?.activePlayerId) return;
    const canAutoWithCurrentIdentity = tc.activePlayerId === currentClientPlayerId;
    const hasValidCurrentIdentity = Boolean(
      currentClientPlayerId && gameState.players.some((player) => player.id === currentClientPlayerId),
    );
    const canAutoWithHostIdentityRecovery = isHost && !hasValidCurrentIdentity;
    if (!canAutoWithCurrentIdentity && !canAutoWithHostIdentityRecovery) return;
    if ((tc.rollCount ?? 0) > 0) return;
    if (rpcLock.active) return;

    const key = `${gameState.id}:${gameState.turnNumber}:${tc.activePlayerId}`;
    if (lastSpectatorAutoTurnRef.current === key) return;
    const started = endTurn({
      allowWithoutRoll: true,
      allowHostMismatch: canAutoWithHostIdentityRecovery,
    });
    if (started) {
      lastSpectatorAutoTurnRef.current = key;
    }
  }, [
    currentClientPlayerId,
    devPlayAsSpectator,
    endTurn,
    gameState.id,
    gameState.isGameOver,
    gameState.mode,
    gameState.players,
    gameState.turnCycle,
    gameState.turnNumber,
    gameState.winner,
    isHost,
    rpcLock.active,
  ]);

  const rollDiceAction = React.useCallback(() => {
    if (gameState.mode === 'REAL' || !gameState.turnCycle) return;
    if (!ensureActivePlayer(gameState, currentClientPlayerId)) return;
    perf.inc('roll.invocations');
    perf.mark('rollDiceAction');
    const keptMask = cloneMask(gameState.turnCycle.kept);
    const capturedMask = cloneMask(gameState.turnCycle.capturedMask);
    const mask = keptMask.map((flag, index) => flag || capturedMask[index]);
    if (bustTimerRef.current) {
      clearTimeout(bustTimerRef.current);
      bustTimerRef.current = null;
    }
    const rollIntentId = randomId();
    const testDice = dequeueDeterministicTestDice();
    void withRpcLock('DICE_ROLL', rollIntentId, async () => {
      const ok = await sendOnlineCommand(gameState.id, {
        type: 'ROLL_INTENT',
        eventId: rollIntentId,
        playerId: gameState.turnCycle!.activePlayerId!,
        keptMask: mask,
        ...(testDice ? { testDice } : {}),
      });
      if (!ok) {
        const lastTransportError = typeof window !== 'undefined'
          ? window.__LAST_PARTYKIT_COMMAND_ERROR__
          : undefined;
        const rejectionContext = {
          gameId: gameState.id,
          turn: gameState.turnNumber,
          playerId: gameState.turnCycle?.activePlayerId || null,
          eventId: rollIntentId,
          keptMask: mask,
          transportReason: lastTransportError?.reason ?? null,
          transportCode: lastTransportError?.code ?? null,
          transportSource: lastTransportError?.source ?? null,
          transportMessage: lastTransportError?.message ?? null,
        };
        logger.warn('ROLL_INTENT_REJECTED_CONTEXT', rejectionContext);
        recordDebugTrace(
          'turn:ROLL_INTENT_REJECTED_CONTEXT',
          rejectionContext,
          { source: 'useTurnActions', tags: ['turn', 'partykit', 'transport', 'error'] }
        );
        throw new Error('ROLL_INTENT_REJECTED');
      }
    }).catch((err) => {
      const lastTransportError = typeof window !== 'undefined'
        ? window.__LAST_PARTYKIT_COMMAND_ERROR__
        : undefined;
      const failureContext = {
        error: err instanceof Error ? err.message : String(err),
        gameId: gameState.id,
        turn: gameState.turnNumber,
        playerId: gameState.turnCycle?.activePlayerId || null,
        eventId: rollIntentId,
        transportReason: lastTransportError?.reason ?? null,
        transportCode: lastTransportError?.code ?? null,
        transportSource: lastTransportError?.source ?? null,
      };
      logger.error('ROLL_INTENT_FAILED', failureContext);
      recordDebugTrace(
        'turn:ROLL_INTENT_FAILED',
        failureContext,
        { source: 'useTurnActions', tags: ['turn', 'partykit', 'transport', 'error'] }
      );
      refreshSnapshotAfterCommandFailure(
        'turn-roll-rejected',
        lastTransportError?.code ?? null,
        lastTransportError?.reason ?? null,
      );
    });
    perf.end('rollDiceAction');
  }, [currentClientPlayerId, ensureActivePlayer, gameState, refreshSnapshotAfterCommandFailure, withRpcLock]);

  const toggleKeepDie = React.useCallback((index: number) => {
    if (gameState.mode === 'REAL' || !gameState.turnCycle) return;
    if (rpcLock.active && rpcLock.type !== 'DICE_KEEP') return;
    if (gameState.turnCycle.activePlayerId !== currentClientPlayerId) return;
    
    const prevKept = optimisticKeptMaskRef.current
      ? cloneMask(optimisticKeptMaskRef.current)
      : cloneMask(gameState.turnCycle.kept);
    const result = computeToggleKeepResult(gameState, index, prevKept);
    if (!result.ok) {
      if (result.reason === 'CAPTURED_DIE') logger.debug('KEEP_BLOCKED_CAPTURED_DIE', { index });
      if (result.reason === 'NON_SCORING') logger.debug('KEEP_BLOCKED_NON_SCORING', { index });
      if (result.reason === 'NO_ROLL_INDEX') logger.debug('KEEP_BLOCKED_NO_ROLL_INDEX');
      return;
    }

    logger.debug('KEEP_TOGGLE', {
      index,
      diceValue: gameState.turnCycle.dice?.[index],
      prevKept: result.prevKept,
      newKept: result.nextKept,
      keptCount: result.nextKept.filter(Boolean).length,
      enforceGroup: result.enforceGroup,
    });

    optimisticKeptMaskRef.current = cloneMask(result.nextKept);
    queuedKeepMaskRef.current = cloneMask(result.nextKept);
    flushQueuedKeepIntent();
  }, [currentClientPlayerId, flushQueuedKeepIntent, gameState, rpcLock.active, rpcLock.type]);

  const potential = useOptimizedPotential(gameState);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    window.__LAST_POTENTIAL__ = potential;
  }, [potential]);

  const rescueByFiveAvailable = React.useMemo(() => {
    if (!potential || !gameState.turnCycle?.dice) return false;
    if (potential.scoringDice > 0) return false;
    return gameState.settings.rescueByFive && canRescueByFive(gameState.turnCycle.dice, potential.scoringDice);
  }, [gameState.settings.rescueByFive, gameState.turnCycle?.dice, potential]);

  const bankBlockedReason = React.useMemo(() => calculateBankBlockedReason(gameState, potential), [gameState, potential]);

  const undoLastTurn = React.useCallback(() => {
    if (gameState.mode === 'REAL' || !isHost) return;
    const events = gameState.events || [];
    const completedTurns = new Set(events.filter(e => e.type === 'TURN_END').map(e => e.turn));
    if (completedTurns.size === 0) return;
    const target = Math.max(...Array.from(completedTurns.values()));
    dispatch({ type: 'UNDO_TURN', turn: target });
  }, [dispatch, gameState.events, gameState.mode, isHost]);

  React.useEffect(() => () => {
    if (bustTimerRef.current) {
      clearTimeout(bustTimerRef.current);
      bustTimerRef.current = null;
    }
  }, []);

  return {
    rollDice: rollDiceAction,
    toggleKeepDie,
    endTurn,
    undoLastTurn,
    potential,
    bankBlockedReason,
    rescueByFiveAvailable,
    rpcLock: isRpcLocked ? { ...rpcLock, active: true } : rpcLock,
  };
}

export { formatPersistenceErrorMessage } from './useTurnPersistence';
